// meetingRooms/container.js - Updated with handleOpenCreateDialog
import React, { useState, useEffect, useRef } from 'react';
import { updateResourceManagement, addResourceManagement } from 'api';
import MeetingRoomsView from './view';

// Import custom hooks for meeting rooms
import { useMeetingRoomState } from './hooks/useMeetingRoomState';
import { useGeneralInfo } from './hooks/useGeneralInfo';
import { useServices } from './hooks/useServices';
import { useGallery } from './hooks/useGallery';
import { usePricing } from './hooks/usePricing';

const MeetingRoomsContainer = ({
  meetingRooms,
  workAreas,
  services,
  resourceTypes,
  getWorkAreaName,
  getTariffsForResource,
  handleAddResource,
  handleUpdateResource,
  handleDeleteResource,
  handleAddTariff,
  handleUpdateTariff,
  handleDeleteTariff,
  venueInfo,
  isLoading: parentIsLoading
}) => {
  // VAT percentage (if needed for pricing)
  const VAT_RATE = 20;

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Refs to prevent unnecessary re-initialization
  const isSavingRef = useRef(false);
  const lastProcessedMeetingRoomsRef = useRef(null);

  // Get all meeting rooms with additional context
  const allMeetingRooms = (meetingRooms || []).map(meetingRoom => ({
    ...meetingRoom,
    work_area_name: getWorkAreaName ? getWorkAreaName(meetingRoom.work_area_id || meetingRoom.work_area) : 'Unknown'
  }));

  // Check if venue has any meeting rooms
  const hasMeetingRooms = meetingRooms && meetingRooms.length > 0;

  // Meeting Room specific resource type
  const meetingRoomResourceType = resourceTypes ? resourceTypes.find(type => type.value === 'MR') : null;

  // Snackbar helpers
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Initialize custom hooks
  const meetingRoomState = useMeetingRoomState(allMeetingRooms);
  const generalInfo = useGeneralInfo(allMeetingRooms, showSnackbar);
  const servicesHook = useServices(allMeetingRooms, showSnackbar);
  const gallery = useGallery(allMeetingRooms, showSnackbar);
  const pricing = usePricing(allMeetingRooms, showSnackbar);

  // Helper function to get work area slug from ID
  const getWorkAreaSlug = (workAreaId) => {
    const workArea = workAreas.find(wa => wa.id === workAreaId);
    return workArea ? workArea.slug : null;
  };

  // ✅ ADD THIS FUNCTION - Handle opening the create dialog
  const handleOpenCreateDialog = () => {
    meetingRoomState.setShowCreateDialog(true);
  };

  // Handle creation of a new meeting room
  const handleCreateMeetingRoom = async (meetingRoomData) => {
    try {
      meetingRoomState.setIsCreating(true);
      isSavingRef.current = true;

      // Format the resource data for the API
      const workAreaSlug = getWorkAreaSlug(meetingRoomData.work_area);
      
      if (!workAreaSlug) {
        throw new Error('Invalid work area selected');
      }
      
      // Prepare data for API submission
      const requestData = {
        resource: {
          name: meetingRoomData.name,
          resource_type: 'MR',
          work_area_slug: workAreaSlug,
          capacity: meetingRoomData.capacity || 4,
          status: 'IN',
        }
      };
      
      // Call the API endpoint
      const result = await addResourceManagement(requestData);
      
      if (!result || !result.success) {
        throw new Error(result?.error || 'Failed to create meeting room');
      }
      
      // If successful, add the new resource via the parent handler
      const newResource = result.resource;
      if (handleAddResource) {
        handleAddResource(newResource);
      }

      showSnackbar('Meeting room created successfully', 'success');
      meetingRoomState.setShowCreateDialog(false);

      // Expand the newly added meeting room
      meetingRoomState.handleToggleMeetingRoom(newResource.slug);

    } catch (error) {
      console.error('Error creating meeting room:', error);
      showSnackbar(`Failed to create meeting room: ${error.message}`, 'error');
    } finally {
      meetingRoomState.setIsCreating(false);
      isSavingRef.current = false;
    }
  };

  // Initialize state from meeting rooms data
  useEffect(() => {
    if (meetingRooms.length > 0 && !isSavingRef.current) {
      // Initialize all hooks with meeting rooms data
      generalInfo.initializeGeneralInfo(allMeetingRooms);
      servicesHook.initializeServices(allMeetingRooms);
      gallery.initializeGallery(allMeetingRooms);
      pricing.initializePricing(allMeetingRooms);
    }
  }, [meetingRooms.length, allMeetingRooms.map(mr => mr.slug).join(',')]); // Better dependency array

  // Prepare props for the view
  const viewProps = {
    // Data
    meetingRooms: allMeetingRooms,
    meetingRoomResourceType,
    workAreas,
    services,
    venueInfo,
    parentIsLoading,

    // Meeting Room State
    expandedMeetingRooms: meetingRoomState.expandedMeetingRooms,
    showCreateDialog: meetingRoomState.showCreateDialog,
    
    // Snackbar
    snackbar,

    // GeneralInfo props
    generalInfoFormData: generalInfo.generalInfoFormData,
    generalInfoHasChanges: generalInfo.generalInfoHasChanges,
    isSavingGeneralInfo: generalInfo.isSavingGeneralInfo,

    // Services props
    servicesFormData: servicesHook.servicesFormData,
    servicesHasChanges: servicesHook.servicesHasChanges,
    isSavingServices: servicesHook.isSavingServices,

    // Gallery props
    galleryFormData: gallery.galleryFormData,
    newImages: gallery.newImages,
    isSavingGallery: gallery.isSavingGallery,

    // Pricing props
    pricingFormData: pricing.pricingFormData,
    pricingHasChanges: pricing.pricingHasChanges,
    isSavingPricing: pricing.isSavingPricing,
    isLoadingTariffs: pricing.isLoadingTariffs,
    showNewTariffForm: pricing.showNewTariffForm,

    // Computed values
    hasMeetingRooms,
    totalRooms: meetingRooms.length,
    VAT_RATE,

    // Meeting Room State Handlers
    handleToggleMeetingRoom: meetingRoomState.handleToggleMeetingRoom,
    handleCloseCreateDialog: meetingRoomState.handleCloseCreateDialog,
    handleOpenCreateDialog, // ✅ ADD THIS LINE
    handleCreateMeetingRoom,
    
    // Snackbar Handlers
    showSnackbar,
    handleCloseSnackbar,

    // GeneralInfo handlers
    handleGeneralInfoFormChange: generalInfo.handleGeneralInfoFormChange,
    handleSaveGeneralInfo: generalInfo.handleSaveGeneralInfo,
    handleCancelGeneralInfo: generalInfo.handleCancelGeneralInfo,

    // Services handlers
    handleServicesFormChange: servicesHook.handleServicesFormChange,
    handleSaveServices: servicesHook.handleSaveServices,
    handleCancelServices: servicesHook.handleCancelServices,
    handleServiceMoveUp: servicesHook.handleServiceMoveUp,
    handleServiceMoveDown: servicesHook.handleServiceMoveDown,
    handleServiceDescriptionSave: servicesHook.handleServiceDescriptionSave,
    handleAddService: servicesHook.handleAddService,
    handleRemoveService: servicesHook.handleRemoveService,

    // Gallery handlers
    handleSaveGallery: gallery.handleSaveGallery,
    handleCancelGallery: gallery.handleCancelGallery,
    handleImageUpload: gallery.handleImageUpload,
    handleImageDelete: gallery.handleImageDelete,
    handleImageMoveLeft: gallery.handleImageMoveLeft,
    handleImageMoveRight: gallery.handleImageMoveRight,

    // Pricing handlers
    handleCreateTariff: pricing.handleCreateTariff,
    handleUpdateTariff: pricing.handleUpdateTariff,
    handleDeleteTariff: pricing.handleDeleteTariff,
    handleToggleNewTariffForm: pricing.handleToggleNewTariffForm,
    handleSavePricing: pricing.handleSavePricing,
    handleCancelPricing: pricing.handleCancelPricing,
    fetchTariffs: pricing.fetchTariffs,

    // External functions passed through
    getWorkAreaName,
    getTariffsForResource,
    handleDeleteResource,

    // API functions
    updateResourceManagement
  };

  return <MeetingRoomsView {...viewProps} />;
};

export default MeetingRoomsContainer;